import { renderToStream } from '@react-pdf/renderer'
import type { ReactElement } from 'react'
import { z } from 'zod'

export const reportFormatSchema = z.enum(['pdf', 'csv']).default('pdf')

export type ReportFormat = z.infer<typeof reportFormatSchema>

export type CsvValue = string | number | boolean | null | undefined | Date

export interface CsvFileDefinition {
  headers: string[]
  rows: Array<Record<string, CsvValue>>
  delimiter?: string
}

type DownloadResponse = NodeJS.WritableStream & {
  end(chunk?: string): void
  setHeader(name: string, value: string): void
}

function stringifyCsvValue(value: CsvValue, delimiter: string) {
  if (value === null || value === undefined) {
    return ''
  }

  const stringValue =
    value instanceof Date
      ? value.toISOString()
      : String(value)
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')

  const normalizedValue = stringValue.replace(/\n/g, ' ')

  if (
    normalizedValue.includes(delimiter) ||
    normalizedValue.includes('"') ||
    normalizedValue.includes('\n')
  ) {
    return `"${normalizedValue.replace(/"/g, '""')}"`
  }

  return normalizedValue
}

export function buildCsvContent({ headers, rows, delimiter = ';' }: CsvFileDefinition) {
  const csvRows = [headers, ...rows.map(row => headers.map(header => stringifyCsvValue(row[header], delimiter)))]

  return csvRows.map(line => line.join(delimiter)).join('\n')
}

export function buildReportFileName(baseName: string, format: ReportFormat) {
  return `${baseName}.${format.toLowerCase()}`
}

export async function sendPdfDownload(
  response: DownloadResponse,
  fileName: string,
  document: ReactElement,
) {
  const stream = await renderToStream(document)

  response.setHeader('Content-Type', 'application/pdf')
  response.setHeader('Content-Disposition', `attachment; filename="${fileName}"`)

  await new Promise<void>((resolve, reject) => {
    stream.pipe(response)
    stream.on('end', resolve)
    stream.on('error', reject)
  })
}

export async function sendCsvDownload(
  response: DownloadResponse,
  fileName: string,
  csvFile: CsvFileDefinition,
) {
  response.setHeader('Content-Type', 'text/csv; charset=utf-8')
  response.setHeader('Content-Disposition', `attachment; filename="${fileName}"`)
  const csvContent = `\uFEFF${buildCsvContent(csvFile)}`
  
  response.end(csvContent)
  return Promise.resolve()
}
